import { useState } from "react";
import { submitPlacement, type CefrLevel } from "../lib/api";

interface Props {
  currentLevel: CefrLevel | null;
  onDone: (newLevel: CefrLevel, leveledUp: boolean) => void;
  onCancel: () => void;
}

// Re-placement questions are slightly more demanding than the initial
// onboarding set — designed to detect upward movement.
const RQ = [
  { question: "Describe a small change you've made in your routine this month and how it's going.", hint: "Use present perfect + reasons." },
  { question: "Tell me about a recent challenge at work or study and how you handled it.", hint: "Past tense, specific details." },
  { question: "If you could redesign one thing about your city, what would it be and why?", hint: "Hypotheticals + opinions." },
  { question: "What's your take on the future of remote work? Pros and cons in your own words.", hint: "Discourse markers, abstract reasoning." },
];

export function RePlacement({ currentLevel, onDone, onCancel }: Props): JSX.Element {
  const [answers, setAnswers] = useState<string[]>(RQ.map(() => ""));
  const [qIdx, setQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (!answers[qIdx].trim()) {
      setError("Type at least a short answer to continue.");
      return;
    }
    setError(null);
    if (qIdx < RQ.length - 1) {
      setQIdx(qIdx + 1);
      return;
    }
    setSubmitting(true);
    try {
      const r = await submitPlacement(RQ.map((q, i) => ({ question: q.question, answer: answers[i] })));
      const order: CefrLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
      const curIdx = currentLevel ? order.indexOf(currentLevel) : -1;
      const newIdx = order.indexOf(r.level);
      onDone(r.level, newIdx > curIdx);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-2xl border border-sky-700/40 bg-sky-500/5 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Check-in {qIdx + 1}/{RQ.length}</div>
          <h2 className="text-lg font-semibold text-white mt-1">{RQ[qIdx].question}</h2>
          <p className="text-xs text-ink-400 mt-1">{RQ[qIdx].hint}</p>
        </div>
        <button onClick={onCancel} className="text-ink-400 hover:text-white text-xl" aria-label="Close">×</button>
      </div>
      <textarea
        value={answers[qIdx]}
        onChange={(e) => {
          const next = [...answers];
          next[qIdx] = e.target.value;
          setAnswers(next);
        }}
        rows={4}
        placeholder="Answer in English…"
        className="w-full rounded-xl bg-ink-800 border border-ink-700 px-3 py-2 text-sm text-white placeholder-ink-500 focus:outline-none focus:border-sky-500"
      />
      {error && <div className="text-rose-300 text-xs">{error}</div>}
      <div className="flex items-center justify-between">
        {qIdx > 0 ? (
          <button onClick={() => setQIdx(qIdx - 1)} className="text-xs text-ink-400 hover:text-white">← Back</button>
        ) : <div />}
        <button
          onClick={submit}
          disabled={submitting}
          className="rounded-xl bg-sky-500 hover:bg-sky-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-40"
        >
          {qIdx < RQ.length - 1 ? "Next →" : submitting ? "Scoring…" : "Finish check-in"}
        </button>
      </div>
    </div>
  );
}
